/**
 * Cross-platform social + Personal-MP analytics contract.
 *
 * ONLY the shape of the events (types + prop enums) and pure derivations
 * (buckets, prop mappers, `?ref=`/`?ch=` parsers) live here. The web-side
 * `flashka-web/src/lib/social/analytics.ts` layers `captureSocial`,
 * `useShareAttributionCapture`, and the sessionStorage attribution store on
 * top; a future mobile analytics module will do the same over its own PostHog
 * (or Adjust) transport, reading the same event names and shapes from this
 * file.
 *
 * ### Hard rules (shared, both platforms)
 *   - **PII**: never send a raw `birthYear`, a handle, a display name, or an
 *     email as an event prop. `targetUserId` (a Clerk userId — the same class
 *     as `distinct_id`) is the ONE allowed identifier. Ages / counts / lifetime
 *     MP go out BUCKETED (`ageBucket`, `countBucket`, `mpBucket`).
 *   - **Never change behavior**: captures are fire-and-forget (no `await`),
 *     happen AFTER the action they describe, and any non-trivial prop
 *     derivation at the call site is wrapped in `try { } catch { }`.
 *   - **Optimistic mutations settle first**: follow / unfollow / block events
 *     fire from `onSuccess`, never on press.
 */

import type { SocialGateState } from './consent-state';
import type { SocialLeaderboardScope, SocialVisibility } from './types';

// ---------------------------------------------------------------------------
// Buckets — the PII-safe shapes every numeric/age dimension goes out as
// ---------------------------------------------------------------------------

/** Age band. Derived from an age or a birthYear; the YEAR never leaves. */
export type SocialAgeBucket =
  | 'under16'
  | '16_17'
  | '18_21'
  | '22_25'
  | '26_35'
  | '36plus';

/** Follower / following count band. */
export type SocialCountBucket = '0' | '1_2' | '3_6' | '7_14' | '15plus';

/** Lifetime mastery-points band. */
export type SocialMpBucket = '0' | 'lt1k' | '1k_10k' | '10k_100k' | '100kplus';

export function ageBucketFromAge(age: number): SocialAgeBucket {
  if (age < 16) return 'under16';
  if (age < 18) return '16_17';
  if (age < 22) return '18_21';
  if (age < 26) return '22_25';
  if (age < 36) return '26_35';
  return '36plus';
}

/**
 * Bucket a stored birthYear. `undefined` when there is no year to bucket — the
 * caller then OMITS the property rather than sending a placeholder.
 */
export function ageBucketFromBirthYear(
  birthYear: number | null | undefined,
  now: Date = new Date(),
): SocialAgeBucket | undefined {
  if (birthYear == null || !Number.isFinite(birthYear)) return undefined;
  return ageBucketFromAge(now.getFullYear() - birthYear);
}

export function countBucket(n: number | null | undefined): SocialCountBucket {
  const v = typeof n === 'number' && Number.isFinite(n) ? n : 0;
  if (v <= 0) return '0';
  if (v <= 2) return '1_2';
  if (v <= 6) return '3_6';
  if (v <= 14) return '7_14';
  return '15plus';
}

export function mpBucket(mp: number | null | undefined): SocialMpBucket {
  const v = typeof mp === 'number' && Number.isFinite(mp) ? mp : 0;
  if (v <= 0) return '0';
  if (v < 1_000) return 'lt1k';
  if (v < 10_000) return '1k_10k';
  if (v < 100_000) return '10k_100k';
  return '100kplus';
}

// ---------------------------------------------------------------------------
// Shared prop enums
// ---------------------------------------------------------------------------

/** Where a follow / unfollow was triggered from. Every call site is wired. */
export type FollowSurface =
  | 'searchRow'
  | 'globalSearchRow'
  | 'suggestionCardProfile'
  | 'suggestionRowFindMates'
  | 'visitorProfile'
  | 'followersPopover'
  | 'followingPopover'
  | 'commonPopover'
  | 'notification'
  | 'lookWhoIsHereBatch'
  /** The recipient's "@owner shared this Set with you — Follow" nudge toast. */
  | 'sharedDeckToast'
  /** The owner's "you shared {Set} with @someone" inbox row. */
  | 'deckOpenedNotification'
  /** The "you don't follow N people in this Group" nudge toast. */
  | 'followEveryoneToast';

/**
 * The two surfaces that follow in BATCH (`useFollowManyMutation`). Both mint
 * one `social_follow_added` per new edge, so the per-edge `surface` has to say
 * which of them it was — otherwise the toast disappears into the dialog's row.
 */
export type FollowManySurface = Extract<
  FollowSurface,
  'lookWhoIsHereBatch' | 'followEveryoneToast'
>;

export type BlockSurface =
  | 'visitorMenu'
  | 'searchRow'
  | 'visitorProfile'
  | 'settings'
  | 'sgSafety';

/** Which search dimension surfaced a row (mirrors `SocialSearchResult`). */
export type SearchMatchedOn = 'handle' | 'name' | 'university' | 'field';

/** Which Complete-Profile / Edit open path fired the dialog. */
export type ProfileDialogTrigger =
  | 'proactive'
  | 'contextual'
  | 'afterAgeGate'
  | 'editButton'
  | 'notification'
  /** The Home leaderboard widget's add-university CTA (University tab). */
  | 'leaderboardWidget';

/** Which of the three Find-Mates zero-result flavours rendered. */
export type EmptyMatesFlavour =
  | 'ownSchoolFirst'
  | 'fieldNoResults'
  | 'generic'
  /** The Home leaderboard widget's empty-state invite CTA. */
  | 'leaderboardWidget';

/** How the visitor got to a profile page. */
export type VisitorProfileSource =
  | 'search'
  | 'suggestion'
  | 'popover'
  | 'notification'
  | 'shareLink'
  | 'direct';

/** Router-state key threading {@link VisitorProfileSource} through navigation. */
export type SocialNavState = { socialSource?: VisitorProfileSource };

export type SocialErrorScope =
  | 'api'
  | 'consent'
  | 'shareRender'
  | 'clipboard'
  | 'nativeShare';

/** Study surface a personal-MP session ran on. */
export type PersonalMpSessionKind = 'sr' | 'fr' | 'quiz' | 'oral' | 'case';

/** Suggestion tier as computed by the Postgres suggestions query (1 = strongest). */
export type SuggestionTier = 1 | 2 | 3 | 4;

/** The Home leaderboard widget's CTAs — one event, one funnel per value. */
export type LeaderboardWidgetCta =
  /** Locked state → "set up your profile" (routes into the age-gate flow). */
  | 'unlock'
  /** Empty Mates board → Find Mates page. */
  | 'findMates'
  /** Empty University board → copy invite link (success additionally fires
   *  `social_invite_link_copied` with flavour `leaderboardWidget`). */
  | 'invite'
  /** University tab without a university → profile edit dialog. */
  | 'addUniversity'
  /**
   * A row on the "Top universities" board → Find Mates, pre-filtered to that
   * school (`?q=<school>&src=leaderboard`). Deliberately NOT read as a
   * multi-step funnel — at this volume a four-step funnel is noise, and
   * PostHog's conversion window would credit the board with follows made
   * days later from an unrelated search. The board is judged on two ratios
   * instead: `scope_changed(universities)` over all `scope_changed` (does
   * anyone look?), and `follow_added` with
   * `searchEntry = leaderboardUniversity` over all `follow_added` (does it
   * build the graph?). The follow carries its own attribution — see
   * {@link SearchEntry}.
   */
  | 'universityRow';

/**
 * How the user arrived at a Find Mates search. `chipPrefill` = a school /
 * field-of-study chip on a profile seeded `?q=`; `leaderboardUniversity` = a
 * row on the Home leaderboard's "Top universities" board
 * (`?q=<school>&src=leaderboard`); everything else is `typed`.
 *
 * Reported twice on purpose: on `social_search_performed` (the impression)
 * and on `social_follow_added` (the outcome), so "which entry point converts"
 * is a breakdown of ONE event rather than an inference from event order.
 */
export type SearchEntry = 'typed' | 'chipPrefill' | 'leaderboardUniversity';

// ---------------------------------------------------------------------------
// The event contract — props typed per event
// ---------------------------------------------------------------------------

/**
 * Every social / personal-MP event and its exact props. Property keys are
 * camelCase (the repo's dominant convention); event names are snake_case.
 *
 * Optional props mean "omit when not derivable at this call site" — never send
 * `null`/`undefined` placeholders, PostHog would index them as values.
 */
export type SocialAnalyticsEventProps = {
  // ── A. Consent & age gate ───────────────────────────────────────────────
  social_age_gate_shown: {
    surface: 'proactiveModal' | 'profileTab';
    shownCount: number;
    isSignupDayReturnSession?: boolean;
  };
  social_age_gate_submitted: {
    /**
     * Bucketed from the entered age. OMITTED for the `<14` branch so nothing
     * about a rejected under-14 user leaks into the warehouse (ToS: Flashka
     * never knowingly collects data from under-14). When absent the funnel
     * still counts the submission (via `eligible=false`).
     */
    ageBucket?: SocialAgeBucket;
    eligible: boolean;
  };
  social_consent_granted: {
    /**
     * Which surface produced the consent moment:
     *   - `ageGate` — the age-gate Confirm (proactive modal or profile-tab gate).
     *   - `ageUpdate` — an under-16 user updated their age to ≥16 in the
     *     reduced Edit dialog on their own profile.
     */
    source: 'ageGate' | 'ageUpdate';
  };

  // ── B. Profile fuel ─────────────────────────────────────────────────────
  social_profile_dialog_shown: {
    mode: 'complete' | 'edit';
    trigger: ProfileDialogTrigger;
    shownCount?: number;
  };
  social_profile_saved: {
    mode: 'complete' | 'edit';
    trigger: ProfileDialogTrigger;
    isFirstCompletion: boolean;
    hasUniversity: boolean;
    hasField: boolean;
    changedUniversity: boolean;
    changedField: boolean;
    changedUsername: boolean;
    changedName: boolean;
    changedAvatar: boolean;
    changedVisibility: boolean;
    universityId?: string;
    fieldOfStudy?: string;
    visibility: SocialVisibility;
  };
  social_profile_save_failed: {
    reason:
      | 'usernameTaken'
      | 'usernameTooShort'
      | 'usernameTooLong'
      | 'invalidCharacters'
      | 'reprojectFailed'
      | 'unknown';
  };
  social_university_submitted: {
    userCountry?: string;
    locale: string;
    resultCountSeen: number;
  };

  // ── C. Discovery & follow ───────────────────────────────────────────────
  social_search_performed: {
    queryLength: number;
    hasAtPrefix: boolean;
    resultCount: number;
    isZeroResult: boolean;
    latencyMs?: number;
    /** How the user arrived at this search — see {@link SearchEntry}. */
    entry: SearchEntry;
    emptyStateFlavour?: EmptyMatesFlavour;
  };
  social_follow_added: {
    surface: FollowSurface;
    targetUserId: string;
    position?: number;
    sharesUniversity?: boolean;
    sharesField?: boolean;
    /**
     * Server truth: the target ALREADY followed the actor, so this follow
     * completed a mutual pair. Read off the follow response (`isFollowBack` /
     * `followedBack`) on every surface — it is the follow-back rate's
     * numerator. Absent only when the worker predates the field.
     */
    isFollowBack?: boolean;
    suggestionTier?: SuggestionTier;
    commonCount?: number;
    /**
     * `searchRow` only — which search dimension surfaced the row that got
     * followed. Deliberately reported on the FOLLOW (the outcome) rather than
     * on the search (the impression): "do people find mates by name, school,
     * or field?" is a question about what actually converts.
     */
    matchedOn?: SearchMatchedOn;
    /**
     * `searchRow` only — the entry point of the search whose RESULTS were on
     * screen when the follow happened. A prefilled search keeps its entry
     * (`leaderboardUniversity` / `chipPrefill`) only until the user edits the
     * query; from then on every follow is `typed`. This is the attribution
     * the "Top universities" board is judged on — no time window involved.
     */
    searchEntry?: SearchEntry;
  };
  social_follow_removed: {
    surface: FollowSurface;
    targetUserId: string;
  };
  social_follow_many_submitted: {
    offeredCount: number;
    selectedCount: number;
    followedCount: number;
    deselectedAllFirst: boolean;
    /** Which batch-follow surface submitted. Absent on clients that predate
     *  the second one (the Follow-Everyone toast). */
    surface?: 'lookWhoIsHere' | 'followEveryoneToast';
  };
  social_look_who_is_here_shown: {
    mateCount: number;
    universityId?: string;
    fieldOfStudy?: string;
  };
  social_suggestions_shown: {
    surface: 'profileCarousel' | 'findMatesIdle';
    count: number;
    tier1Count: number;
    tier2Count: number;
    tier3Count: number;
    tier4Count: number;
  };
  /**
   * The consented owner opened their OWN profile. Once per mount. Neither web
   * SPA navigations (`$pageview` fires on the initial load only) nor mobile
   * screens (`$screen` autocapture is off) are captured, so without this the
   * most common social action — checking your own profile — was invisible to
   * "weekly active social users".
   */
  social_own_profile_viewed: {
    isProfileComplete: boolean;
    followersBucket: SocialCountBucket;
    followingBucket: SocialCountBucket;
    /** Mutual follows (mates). Absent when the worker predates `mutualCount`. */
    mutualBucket?: SocialCountBucket;
  };
  social_visitor_profile_viewed: {
    source: VisitorProfileSource;
    isFollowing: boolean;
    hasSnapshot: boolean;
    followersBucket: SocialCountBucket;
    commonCount?: number;
  };

  // ── D. Viral loop ───────────────────────────────────────────────────────
  social_share_dialog_opened: Record<string, never>;
  social_share_channel_clicked: {
    /** `share`: the phone's one Share button (card + link in the OS sheet). */
    channel: 'share' | 'copy' | 'download' | 'tiktok' | 'whatsapp' | 'instagram';
    /**
     * HOW the tap was serviced.
     *
     * `directApp` (added 2026-08-26 for the mobile share card) is a
     * `react-native-share` `shareSingle` straight into Instagram / WhatsApp —
     * a route web has no equivalent for. It is captured on PRESS like every
     * other path, so it records the INTENT to go direct; when the native call
     * rejects (app not installed, OS refusal) the mobile handler falls back to
     * the OS sheet and that recovery shows up as a `social_error`, not as a
     * second channel event.
     */
    path: 'nativeSheet' | 'downloadFallback' | 'link' | 'directApp';
    /** Always `true` on mobile; a coarse-pointer probe on web. */
    isTouch: boolean;
    /**
     * `true` = the own-profile share dialog, `false` = the visitor header's
     * copy-link button. Required, because BOTH mint `?ref=share&ch=…` URLs that
     * come back as `social_share_link_landed`: leaving the visitor one
     * uncounted would inflate `landed / created` — i.e. the K factor — with
     * links nobody was recorded as creating.
     */
    isOwnProfile: boolean;
  };
  social_invite_link_copied: {
    emptyStateFlavour: EmptyMatesFlavour;
  };
  social_share_link_landed: {
    ref: ShareAttributionRef;
    isSignedIn: boolean;
    /**
     * Did the link actually resolve to a profile? OPTIONAL because only the
     * profile page can answer it — the route-agnostic signed-out capture in
     * `useShareAttributionCapture` omits it rather than guessing. Absent =
     * "not observable from where this fired", never "no".
     */
    targetResolved?: boolean;
    channel?: ShareAttributionChannel;
    lang?: string;
  };

  // ── E. Reciprocity ──────────────────────────────────────────────────────
  social_notification_bell_opened: {
    socialUnseenCount: number;
    sgHasUnread: boolean;
    /** Unread SG activities across groups — the SG share of the bell badge. */
    sgUnreadCount: number;
    totalItems: number;
  };

  // ── F. Personal MP ──────────────────────────────────────────────────────
  personal_mp_session_completed: {
    sessionMp: number;
    todayMp: number;
    sessionKind: PersonalMpSessionKind;
    wasNaturalEnd: boolean;
    isPersonalOnly: boolean;
  };
  personal_mp_claimed: {
    sessionMp: number;
    todayMp: number;
    sessionKind?: PersonalMpSessionKind;
  };

  // ── G. Safety ───────────────────────────────────────────────────────────
  social_block_toggled: {
    block: boolean;
    surface: BlockSurface;
    targetUserId: string;
    wasFollowing?: boolean;
  };
  social_report_submitted: Record<string, never>;

  // ── H. Errors ───────────────────────────────────────────────────────────
  social_error: {
    scope: SocialErrorScope;
    endpoint?: string;
    status?: number;
    code?: string;
  };

  // ── I. Home leaderboard widget ──────────────────────────────────────────
  // No impression event: the widget is on Home, so a view is ~one per app
  // open and was the single most expensive social event. NOTE there is no
  // ready-made denominator either: web captures `$pageview` on the initial
  // load only (posthog-js `capture_pageview: true`, no history-change
  // capture) and mobile has `$screen` autocapture off. "Saw the leaderboard"
  // is therefore not measurable — `scope_changed` / `expanded` measure
  // ENGAGEMENT with it, and Home opens ≈ `Opened-App` (mobile) / the initial
  // `$pageview` (web).
  /** Tab switch. `scope` is the DESTINATION tab. */
  social_leaderboard_scope_changed: {
    scope: SocialLeaderboardScope;
  };
  social_leaderboard_expanded: {
    scope: SocialLeaderboardScope;
    /** Rows loaded at expand time (paging artifact, not board size). */
    rowCount: number;
    viewerRank?: number;
  };
  /** Per-board opt-out toggled from the settings gear (widget or expanded). */
  social_leaderboard_privacy_toggled: {
    board: SocialLeaderboardScope;
    hidden: boolean;
  };
  /** Any of the widget's CTAs — see {@link LeaderboardWidgetCta}. */
  social_leaderboard_cta_clicked: {
    cta: LeaderboardWidgetCta;
    scope: SocialLeaderboardScope;
    /** `universityRow` only — which school was tapped. Not PII (a seeded id). */
    universityId?: string;
    /** `universityRow` only — that school's position on the board. */
    rank?: number;
    /** `universityRow` only — was it the viewer's own school? */
    isOwnUniversity?: boolean;
  };

  // ── H. Bridge pack — share ↔ study groups ↔ follows ─────────────────────
  //
  // 96% of people who share a Set follow nobody, and 3% of sharers are in a
  // study group. These seven events answer whether the bridges between those
  // three surfaces convert: the share sheet offering study groups, the group
  // inviting mates, the two follow nudges, and whether a recipient's import
  // ever reaches the owner at all.

  /** The share sheet rendered its study-group bridge. `groupCount` is how many
   *  groups the sharer is in — 0 is the "create a group with this Set" case. */
  social_share_bridge_shown: { groupCount: number };
  /** Which affordance in the share sheet the sharer actually used. The
   *  pre-existing link/WhatsApp/Telegram actions are reported here too, so the
   *  bridge's share is a breakdown of one event rather than a cross-event
   *  inference. `nativeShare`: the phone's own share sheet (it replaces the
   *  link/WhatsApp/Telegram buttons there); picking Copy in it reports `copyLink`. */
  social_share_bridge_used: {
    action:
      | 'shareToGroups'
      | 'createGroup'
      | 'copyLink'
      | 'whatsapp'
      | 'telegram'
      | 'nativeShare';
    groupCount?: number;
  };
  /** The "Invite your Mates" dialog opened. `mateCount` is how many mutuals it
   *  had to offer — a zero here is the product's real problem, not a bug. */
  social_group_invite_dialog_shown: {
    source: GroupInviteSource;
    mateCount: number;
  };
  social_group_invite_sent: {
    offeredCount: number;
    selectedCount: number;
    /** Did the user clear the pre-selection before picking? Same signal as
     *  the Look-Who-Is-Here dialog: it says the default was wrong. */
    deselectedAllFirst: boolean;
    source: GroupInviteSource;
  };
  /** A follow nudge rendered. `count` is how many people it is about. */
  social_nudge_shown: {
    nudge: SocialNudge;
    /** What brought it back — only the repeatable nudges carry one. */
    trigger?: 'secondOpen' | 'newMember' | 'deckAdded';
    count: number;
  };
  social_nudge_dismissed: { nudge: SocialNudge };
  /**
   * A recipient imported a shared Set from `/d/:code` — the top of the
   * share→follow bridge, and the denominator the `sharedDeckRecipient` nudge
   * and `deckOpenedNotification` follows are judged against.
   *
   * `pushed` is whether the OWNER was actually told. Everything else is the
   * reason they were not, so a low nudge rate can be read as "the bridge does
   * not convert" vs "the bridge never fired": `self`, `opener-not-consented`,
   * `owner-not-consented`, `already-following`, `blocked` (the server's five),
   * `owner-not-social` and `unnamed` (the client never asked), `http-<status>`
   * (the endpoint refused) and `failed` (the request never landed).
   *
   * SCOPE — this is the IMPORT, not the landing. A signed-out visitor sitting
   * on the sign-up CTA has imported nothing and is not counted here; neither
   * client reports one.
   */
  social_shared_deck_opened: {
    pushed: boolean;
    reason?: SharedDeckOpenedReason;
  };
};

/** Why the owner was, or was not, told that their Set was imported. */
export type SharedDeckOpenedReason =
  // The server answered.
  | 'self'
  | 'opener-not-consented'
  | 'owner-not-consented'
  | 'already-following'
  | 'blocked'
  // The client never asked.
  /** The snapshot carries no `owner` — the sharer is not on the social layer. */
  | 'owner-not-social'
  /** An unnamed Set; the notification row is built around the name. */
  | 'unnamed'
  // The request itself.
  /** The endpoint answered with a non-2xx. */
  | `http-${number}`
  /** The request never landed (offline, DNS, abort). */
  | 'failed';

/** Where the "Invite your Mates" dialog was opened from. */
export type GroupInviteSource =
  | 'groupPage'
  | 'createGroup'
  | 'createGroupFromShare'
  | 'dashboardTile';

/** The two follow nudges — the shared-Set recipient's and the group's. */
export type SocialNudge = 'sharedDeckRecipient' | 'followEveryone';

export type SocialAnalyticsEvent = keyof SocialAnalyticsEventProps;

// ---------------------------------------------------------------------------
// Share attribution — `?ref=` / `?ch=` parsing
// ---------------------------------------------------------------------------

/** Which affordance produced the link the visitor landed on. */
export type ShareAttributionRef = 'share' | 'invite';
/** Which channel it went out through (mirrors `ProfileShareChannel`). */
export type ShareAttributionChannel = 'copy' | 'wa' | 'native' | 'ig' | 'tiktok';

const SHARE_REFS: readonly string[] = ['share', 'invite'];
const SHARE_CHANNELS: readonly string[] = ['copy', 'wa', 'native', 'ig', 'tiktok'];

/** Narrow a raw `?ref=` value, or `null` when it isn't one of ours. */
export function parseShareAttributionRef(
  raw: string | null | undefined,
): ShareAttributionRef | null {
  return raw && SHARE_REFS.includes(raw) ? (raw as ShareAttributionRef) : null;
}

/** Narrow a raw `?ch=` value, or `undefined` when absent / unrecognised. */
export function parseShareAttributionChannel(
  raw: string | null | undefined,
): ShareAttributionChannel | undefined {
  return raw && SHARE_CHANNELS.includes(raw)
    ? (raw as ShareAttributionChannel)
    : undefined;
}

// ---------------------------------------------------------------------------
// Prop mappers
// ---------------------------------------------------------------------------

/** `deriveSocialGateState`'s kebab states, in the analytics vocabulary. */
export function socialStateProp(
  state: SocialGateState,
): 'unconsented' | 'underAge' | 'needsUnlock' | 'consented' {
  switch (state) {
    case 'under-age':
      return 'underAge';
    case 'needs-unlock':
      return 'needsUnlock';
    default:
      return state;
  }
}

/** `hidden_from_search` reads as `hiddenFromSearch` in analytics. */
export function socialVisibilityProp(
  visibility: SocialVisibility | null | undefined,
): 'public' | 'hiddenFromSearch' | undefined {
  if (!visibility) return undefined;
  return visibility === 'hidden_from_search' ? 'hiddenFromSearch' : 'public';
}

/** ISO timestamp helper for `$set_once` date properties. */
export function nowISO(): string {
  return new Date().toISOString();
}
